/**
 * Teltonika units in the console (ADR 0032, ADR 0034): device family, immobiliser state, card-list sync
 * status with the read-only output-control check, the TCT setup checklist and the vehicle's analog calibration.
 *
 * ADR 0003: nothing here can act on a vehicle. There is no button, route or provider call that drives an
 * output (DOUT) or the immobiliser; the immobiliser state is shown only, and the console says that it only
 * prevents the first start without a card. The output-control check is read-only: the fix is made on the unit.
 */
import {
  ANALOG_LIMITS,
  BATTERY_SOURCES,
  LIFT_SOURCES,
  calibrationIssues,
  chargeLevelV,
  deviceFamily,
  roleAtLeast,
  suggestedLowBatteryV,
  teltonikaModel,
  type BatterySource,
  type CalibrationIssue,
  type Device,
  type LiftSource,
  type Vehicle,
  type VehicleAnalogCalibration,
} from "@fts/core";
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import "../styles/teltonika.css";
import { Dialog, FormError, FormField, fieldAria } from "../components/Dialog";
import { Icon } from "../components/Icon";
import { useFeedback } from "../components/Toast";
import { Note, Panel } from "../components/ui";
import type { AnalogCalibrationRecord, DeviceImmobilizer, TeltonikaSyncStatus } from "../data/provider";
import { useI18n, type MessageKey } from "../i18n";
import { formatDateTime, relativeTime } from "../lib/format";
import { canManageFleet } from "../lib/permissions";
import { useSession } from "../state/session";

/* ---------------- family ---------------- */

export function DeviceFamilyBadge({ device }: { device: Pick<Device, "deviceId" | "hw"> }) {
  const { t } = useI18n();
  const fam = deviceFamily(device);
  const label = fam === "teltonika" ? t("deviceFamily.teltonikaModel", { model: teltonikaModel(device.hw) }) : t(`deviceFamily.${fam}`);
  return <span className={`badge tone-${fam === "teltonika" ? "info" : "neutral"}`}>{label}</span>;
}

function Row({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="dl-row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function When({ iso }: { iso: string | null }) {
  const { t, locale } = useI18n();
  if (!iso) return <span className="muted">{t("common.empty")}</span>;
  return (
    <time dateTime={iso} title={formatDateTime(iso, locale) ?? undefined}>
      {relativeTime(iso, locale) ?? formatDateTime(iso, locale)}
    </time>
  );
}

/* ---------------- immobiliser (display only) ---------------- */

const IMMO_TONE = { no_card: "neutral", card: "info", authorized: "ok" } as const;

export function ImmobilizerRows({ device }: { device: Device }) {
  const { t } = useI18n();
  const { provider } = useSession();
  const [immo, setImmo] = useState<DeviceImmobilizer | null>(null);
  useEffect(() => {
    let off = false;
    provider.getImmobilizer(device.deviceId).then((r) => !off && setImmo(r), () => !off && setImmo({ state: null, at: null }));
    return () => {
      off = true;
    };
  }, [provider, device.deviceId]);
  return (
    <Row label={t("teltonika.immo.label")}>
      {immo?.state ? <span className={`badge tone-${IMMO_TONE[immo.state]}`}>{t(`teltonika.immo.${immo.state}`)}</span> : <span className="muted">{t("teltonika.immo.unknown")}</span>}
      <p className="field-hint">{t("teltonika.immo.note")}</p>
    </Row>
  );
}

/* ---------------- card-list sync status ---------------- */

/** Safety (İSG) and above see the card-list delivery of a Teltonika unit (the same rule as the delivery log). */
export const canSeeTeltonikaSync = (role: Parameters<typeof canManageFleet>[0], platformAdmin: boolean) => platformAdmin || (!!role && roleAtLeast(role, "safety"));

const UNSAFE_KEY: Record<string, MessageKey> = {
  green_driving: "teltonika.scenario.green_driving",
  overspeeding: "teltonika.scenario.overspeeding",
  excessive_idling: "teltonika.scenario.excessive_idling",
  jamming: "teltonika.scenario.jamming",
};

export function TeltonikaSyncPanel({ device }: { device: Device }) {
  const { t, locale } = useI18n();
  const { provider } = useSession();
  const [status, setStatus] = useState<TeltonikaSyncStatus | null>(null);
  const [error, setError] = useState(false);
  const load = useCallback(() => {
    provider.getTeltonikaStatus(device.deviceId).then(
      (s) => {
        setStatus(s);
        setError(false);
      },
      () => setError(true),
    );
  }, [provider, device.deviceId]);
  useEffect(() => load(), [load]);
  const n = (v: number) => v.toLocaleString(locale);
  return (
    <Panel
      title={t("teltonika.sync.title")}
      meta={
        <button type="button" className="icon-button" onClick={load} title={t("common.retry")}>
          <Icon name="refresh" size={16} label={t("common.retry")} />
        </button>
      }
    >
      {error && !status ? (
        <p className="muted">{t("teltonika.sync.loadError")}</p>
      ) : !status ? (
        <p className="muted">{t("common.loading")}</p>
      ) : (
        <>
          <dl className="dl">
            <Row label={t("teltonika.sync.state")}>
              {!status.hasCardReader ? (
                <span className="badge tone-neutral">{t("teltonika.sync.noReader")}</span>
              ) : status.inSync ? (
                <span className="badge tone-ok">{t("teltonika.sync.inSync")}</span>
              ) : (
                <span className="badge tone-warn">{t("teltonika.sync.pending")}</span>
              )}
            </Row>
            <Row label={t("teltonika.sync.version")}>
              <span className="num">{t("teltonika.sync.versionValue", { ver: status.listVer ?? "—", acked: status.ackedVer ?? "—" })}</span>
            </Row>
            <Row label={t("teltonika.sync.cards")}>
              <span className="num">{n(status.cards)}</span>
            </Row>
            <Row label={t("teltonika.sync.lastSync")}>
              <When iso={status.lastSyncAt} />
            </Row>
            <Row label={t("teltonika.sync.changes")}>
              {status.pendingAdd + status.pendingRemove + status.slotsUnknown === 0 ? (
                <span className="muted">{t("teltonika.sync.noChanges")}</span>
              ) : (
                <span>{t("teltonika.sync.changesValue", { add: n(status.pendingAdd), remove: n(status.pendingRemove), unknown: n(status.slotsUnknown) })}</span>
              )}
            </Row>
            <Row label={t("teltonika.sync.failures")}>
              {status.lastFailure ? (
                <span>
                  {status.failures > 0
                    ? t("teltonika.sync.failureValue", { count: n(status.failures), error: status.lastFailure.error ?? t("common.empty") })
                    : t("teltonika.sync.lastFailure", { error: status.lastFailure.error ?? t("common.empty") })} · <When iso={status.lastFailure.at} />
                </span>
              ) : (
                <span className="muted">{t("teltonika.sync.noFailures")}</span>
              )}
            </Row>
            {status.retryAt && (
              <Row label={t("teltonika.sync.retryAt")}>
                <When iso={status.retryAt} />
              </Row>
            )}
            <Row label={t("teltonika.sync.readback")}>
              <When iso={status.reconciledAt} />
            </Row>
            <Row label={t("teltonika.safety.label")}>
              {status.unsafe === null ? (
                <span className="muted">{t("teltonika.safety.never")}</span>
              ) : status.unsafe.length === 0 ? (
                <span className="badge tone-ok">{t("teltonika.safety.ok")}</span>
              ) : (
                <span className="badge tone-crit">{t("teltonika.safety.unsafe", { scenarios: status.unsafe.map((x) => (UNSAFE_KEY[x] ? t(UNSAFE_KEY[x]) : x)).join(", ") })}</span>
              )}
              {status.safetyCheckedAt && (
                <span className="muted small">
                  {" "}
                  · <When iso={status.safetyCheckedAt} />
                </span>
              )}
            </Row>
          </dl>
          {status.unsafe && status.unsafe.length > 0 && (
            <p className="warning-banner" role="alert">
              <Icon name="alert" size={18} />
              <span>{t("teltonika.safety.fix")}</span>
            </p>
          )}
          <p className="field-hint">{t("teltonika.sync.note")}</p>
        </>
      )}
    </Panel>
  );
}

/* ---------------- TCT setup checklist (docs/ops/teltonika-gateway.md) ---------------- */

/** The site setup card of docs/ops/teltonika-gateway.md as a checklist (platform / Expartise roles). */
export const TCT_STEPS = [
  "wiring",
  "server",
  "certificate",
  "protocol",
  "acquisition",
  "io",
  "analog",
  "reader",
  "immobilizer",
  "crash",
  "scenarios",
  "commands",
  "register",
  "cards",
  "verify",
] as const;

export function TctChecklistPanel({ device }: { device: Device }) {
  const { t } = useI18n();
  const [done, setDone] = useState<Set<string>>(new Set());
  const toggle = (k: string) =>
    setDone((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  return (
    <Panel title={t("tct.title")} meta={<span className="muted small">{t("tct.progress", { done: done.size, total: TCT_STEPS.length })}</span>}>
      <p className="panel-text muted">{t("tct.intro", { device: device.deviceId })}</p>
      <ol className="tct-list">
        {TCT_STEPS.map((k) => (
          <li key={k}>
            <label className="check-row">
              <input type="checkbox" checked={done.has(k)} onChange={() => toggle(k)} />
              <span>
                <strong>{t(`tct.${k}.title`)}</strong>
                <span className="field-hint">{t(`tct.${k}.body`)}</span>
              </span>
            </label>
          </li>
        ))}
      </ol>
      <Note>{t("tct.note")}</Note>
    </Panel>
  );
}

/* ---------------- analog calibration of a vehicle ---------------- */

const ISSUE_KEY: Record<CalibrationIssue, MessageKey> = {
  same_channel: "calib.issue.same_channel",
  bat_scale: "calib.issue.bat_scale",
  bat_offset: "calib.issue.bat_offset",
  bat_low_v: "calib.issue.bat_low_v",
  lift_scale: "calib.issue.lift_scale",
  lift_offset: "calib.issue.lift_offset",
  lift_thresholds: "calib.issue.lift_thresholds",
  lift_hysteresis: "calib.issue.lift_hysteresis",
  lift_drive_kmh: "calib.issue.lift_drive_kmh",
  charge_rise_v: "calib.issue.charge_rise_v",
  charge_v: "calib.issue.charge_v",
};

export function AnalogCalibrationPanel({ vehicle }: { vehicle: Vehicle }) {
  const { t, locale } = useI18n();
  const { provider, role, user } = useSession();
  const canEdit = !!user?.platformAdmin || canManageFleet(role);
  const [cal, setCal] = useState<AnalogCalibrationRecord | null>(null);
  const [editing, setEditing] = useState(false);
  const load = useCallback(() => {
    provider.getAnalogCalibration(vehicle.id).then(setCal, () => setCal(null));
  }, [provider, vehicle.id]);
  useEffect(() => load(), [load]);
  const num = (v: number | null, d = 2) => (v === null ? t("common.empty") : v.toLocaleString(locale, { maximumFractionDigits: d }));
  return (
    <Panel
      title={t("calib.title")}
      meta={
        canEdit && cal ? (
          <button type="button" className="button button-secondary button-sm" onClick={() => setEditing(true)}>
            <Icon name="edit" size={15} />
            {t("common.edit")}
          </button>
        ) : undefined
      }
    >
      {!cal ? (
        <p className="muted">{t("common.loading")}</p>
      ) : (
        <>
          <dl className="dl">
            <Row label={t("calib.batterySource")}>{t(`calib.source.${cal.batterySource}`)}</Row>
            {(cal.batterySource === "ain1" || cal.batterySource === "ain2" || cal.batterySource === "supply") && (
              <Row label={t("calib.formula")}>
                <span className="mono">{t("calib.formulaValue", { scale: num(cal.batScale, 4), offset: num(cal.batOffset) })}</span>
              </Row>
            )}
            <Row label={t("calib.batLowV")}>{cal.batLowV === null ? <span className="muted">{t("calib.alertOff")}</span> : t("calib.volts", { v: num(cal.batLowV, 1) })}</Row>
            <Row label={t("calib.liftSource")}>{t(`calib.lift.${cal.liftSource}`)}</Row>
            {cal.liftSource !== "none" && (
              <Row label={t("calib.liftThresholds")}>
                <span className="num">{t("calib.liftThresholdsValue", { on: num(cal.liftOn), off: num(cal.liftOff) })}</span>
              </Row>
            )}
            <Row label={t("calib.liftDrive")}>{t("calib.kmh", { v: num(cal.liftDriveKmh, 1) })}</Row>
            <Row label={t("calib.chargeV")}>
              {chargeLevelV(cal.chargeV, vehicle.batteryVoltage) === null ? (
                <span className="muted">{t("calib.chargeVUnknown", { rise: num(cal.chargeRiseV, 1) })}</span>
              ) : (
                <>
                  {t("calib.volts", { v: num(chargeLevelV(cal.chargeV, vehicle.batteryVoltage), 1) })}
                  {cal.chargeV === null && <span className="muted small"> · {t("calib.chargeVDerived")}</span>}
                </>
              )}
            </Row>
          </dl>
          {cal.batterySource === "none" && <p className="field-hint">{t("calib.noSource")}</p>}
          <p className="field-hint">{t("calib.supplyNote")}</p>
          {!cal.stored && <p className="field-hint">{t("calib.defaults")}</p>}
        </>
      )}
      {editing && cal && (
        <CalibrationDialog
          vehicle={vehicle}
          initial={cal}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      )}
    </Panel>
  );
}

function parseNum(raw: string): number | null {
  const s = raw.trim().replace(",", ".");
  if (s === "") return null;
  const v = Number(s);
  return Number.isFinite(v) ? v : Number.NaN;
}

export function CalibrationDialog({ vehicle, initial, onClose, onSaved }: { vehicle: Vehicle; initial: VehicleAnalogCalibration; onClose: () => void; onSaved: () => void }) {
  const { t, locale } = useI18n();
  const { provider } = useSession();
  const feedback = useFeedback();
  const str = (v: number | null) => (v === null ? "" : String(v));
  const [f, setF] = useState({
    batterySource: initial.batterySource,
    batScale: str(initial.batScale),
    batOffset: str(initial.batOffset),
    batLowV: str(initial.batLowV),
    liftSource: initial.liftSource,
    liftScale: str(initial.liftScale),
    liftOffset: str(initial.liftOffset),
    liftOn: str(initial.liftOn),
    liftOff: str(initial.liftOff),
    liftDriveKmh: str(initial.liftDriveKmh),
    chargeRiseV: str(initial.chargeRiseV),
    chargeV: str(initial.chargeV),
  });
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const cal: VehicleAnalogCalibration = {
    batterySource: f.batterySource,
    batScale: parseNum(f.batScale) ?? Number.NaN,
    batOffset: parseNum(f.batOffset) ?? 0,
    batLowV: parseNum(f.batLowV),
    liftSource: f.liftSource,
    liftScale: parseNum(f.liftScale) ?? Number.NaN,
    liftOffset: parseNum(f.liftOffset) ?? 0,
    liftOn: parseNum(f.liftOn),
    liftOff: parseNum(f.liftOff),
    liftDriveKmh: parseNum(f.liftDriveKmh) ?? Number.NaN,
    chargeRiseV: parseNum(f.chargeRiseV) ?? Number.NaN,
    chargeV: parseNum(f.chargeV),
  };
  const issues = calibrationIssues(cal);
  const err = (k: CalibrationIssue) => (touched && issues.includes(k) ? t(ISSUE_KEY[k]) : null);
  const suggested = suggestedLowBatteryV(vehicle.batteryVoltage);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const analogBattery = f.batterySource === "ain1" || f.batterySource === "ain2" || f.batterySource === "supply";

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (issues.length > 0) return;
    setBusy(true);
    setError(null);
    try {
      await provider.saveAnalogCalibration(vehicle.id, cal);
      feedback.ok(t("calib.saved", { vehicle: vehicle.fleetNo }));
      onSaved();
    } catch (er) {
      setError(er);
      setBusy(false);
    }
  };

  const numInput = (id: string, k: keyof typeof f, issue: CalibrationIssue | null, extra: { hint?: string; placeholder?: string } = {}) => (
    <input
      {...fieldAria(id, { error: issue ? err(issue) : null, hint: !!extra.hint })}
      inputMode="decimal"
      className="num"
      value={f[k] as string}
      placeholder={extra.placeholder}
      onChange={set(k)}
    />
  );

  return (
    <Dialog
      wide
      title={t("calib.dialogTitle", { vehicle: vehicle.fleetNo })}
      description={t("calib.dialogDescription")}
      onClose={() => !busy && onClose()}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy}>
            {busy ? t("common.working") : t("common.save")}
          </button>
        </>
      }
    >
      <fieldset className="fieldset">
        <legend>{t("calib.batteryGroup")}</legend>
        <FormField id="cal-bat-src" label={t("calib.batterySource")} hint={t("calib.batterySourceHint")}>
          <select {...fieldAria("cal-bat-src", { hint: true })} value={f.batterySource} onChange={(e) => setF((x) => ({ ...x, batterySource: e.target.value as BatterySource }))} data-autofocus>
            {BATTERY_SOURCES.map((s) => (
              <option key={s} value={s}>
                {t(`calib.source.${s}`)}
              </option>
            ))}
          </select>
        </FormField>
        {f.batterySource === "supply" && <p className="warning-banner"><Icon name="alert" size={18} /><span>{t("calib.supplyWarning")}</span></p>}
        {analogBattery && (
          <div className="form-grid-2">
            <FormField id="cal-bat-scale" label={t("calib.scale")} hint={t("calib.scaleHint")} error={err("bat_scale")}>
              {numInput("cal-bat-scale", "batScale", "bat_scale", { hint: "1" })}
            </FormField>
            <FormField id="cal-bat-offset" label={t("calib.offset")} error={err("bat_offset")}>
              {numInput("cal-bat-offset", "batOffset", "bat_offset")}
            </FormField>
          </div>
        )}
        <FormField
          id="cal-bat-low"
          label={t("calib.batLowV")}
          optional
          hint={suggested !== null ? t("calib.batLowHintSuggested", { v: suggested.toLocaleString(locale), nominal: vehicle.batteryVoltage ?? "" }) : t("calib.batLowHint")}
          error={err("bat_low_v")}
        >
          {numInput("cal-bat-low", "batLowV", "bat_low_v", { hint: "1", placeholder: suggested !== null ? String(suggested) : undefined })}
        </FormField>
        <FormField
          id="cal-charge-v"
          label={t("calib.chargeV")}
          optional
          hint={chargeLevelV(null, vehicle.batteryVoltage) !== null ? t("calib.chargeVHintNominal", { v: chargeLevelV(null, vehicle.batteryVoltage)!.toLocaleString(locale), nominal: vehicle.batteryVoltage ?? "" }) : t("calib.chargeVHint")}
          error={err("charge_v")}
        >
          {numInput("cal-charge-v", "chargeV", "charge_v", { hint: "1", placeholder: chargeLevelV(null, vehicle.batteryVoltage)?.toString() })}
        </FormField>
        <FormField id="cal-rise" label={t("calib.chargeRise")} hint={t("calib.chargeRiseHint")} error={err("charge_rise_v")}>
          {numInput("cal-rise", "chargeRiseV", "charge_rise_v", { hint: "1" })}
        </FormField>
      </fieldset>
      <fieldset className="fieldset">
        <legend>{t("calib.liftGroup")}</legend>
        <FormField id="cal-lift-src" label={t("calib.liftSource")} error={err("same_channel")}>
          <select {...fieldAria("cal-lift-src", { error: err("same_channel") })} value={f.liftSource} onChange={(e) => setF((x) => ({ ...x, liftSource: e.target.value as LiftSource }))}>
            {LIFT_SOURCES.map((s) => (
              <option key={s} value={s}>
                {t(`calib.lift.${s}`)}
              </option>
            ))}
          </select>
        </FormField>
        {f.liftSource !== "none" && (
          <>
            <div className="form-grid-2">
              <FormField id="cal-lift-scale" label={t("calib.scale")} error={err("lift_scale")}>
                {numInput("cal-lift-scale", "liftScale", "lift_scale")}
              </FormField>
              <FormField id="cal-lift-offset" label={t("calib.offset")} error={err("lift_offset")}>
                {numInput("cal-lift-offset", "liftOffset", "lift_offset")}
              </FormField>
              <FormField id="cal-lift-on" label={t("calib.liftOn")} error={err("lift_thresholds") ?? err("lift_hysteresis")}>
                {numInput("cal-lift-on", "liftOn", issues.includes("lift_thresholds") ? "lift_thresholds" : "lift_hysteresis")}
              </FormField>
              <FormField id="cal-lift-off" label={t("calib.liftOff")} hint={t("calib.liftOffHint")}>
                {numInput("cal-lift-off", "liftOff", null, { hint: "1" })}
              </FormField>
            </div>
          </>
        )}
        <FormField id="cal-lift-drive" label={t("calib.liftDrive")} hint={t("calib.liftDriveHint")} error={err("lift_drive_kmh")}>
          {numInput("cal-lift-drive", "liftDriveKmh", "lift_drive_kmh", { hint: "1" })}
        </FormField>
      </fieldset>
      <Note>{t("calib.noControl", { min: ANALOG_LIMITS.ainMv.min, max: ANALOG_LIMITS.ainMv.max / 1000 })}</Note>
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}
